import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import {
  colors,
  shadows,
  spaces,
  mixins,
} from '../../core/styles/theme/default';
import { Button, H2 } from '../../primitives';
import { Hidden } from '../../helpers';
import MagneticField from '../../components/MagneticField';

const StyledButton = styled(Button)`
  position: relative;
  z-index: 1;
  min-width: ${rem(268)};
  white-space: nowrap;
  background: transparent;
  border-color: ${colors.nightBlue400};

  ${mixins.MQ('medium')} {
    min-width: ${rem(290)};
  }

  &:hover {
    background: transparent;
  }

  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledDecoration = styled.div`
  position: absolute;
  left: 20%;
  top: 0;
  z-index: 0;

  ${mixins.MQ('medium')} {
    left: 45%;
  }
`;

const NewsletterContent = styled.article(
  ({ theme }: { theme: Theme }) => css`
    position: relative;
    display: flex;
    ${mixins.responsiveStyles({
      'align-items': { zero: '', medium: 'center' },
      'justify-content': { zero: '', medium: 'start' },
      'flex-flow': { zero: 'column', medium: 'row' },
      padding: {
        zero: `${rem(spaces.eta)} ${rem(spaces.epsilon)}`,
        medium: rem(spaces.iota),
      },
    })}
    color: ${colors.white};
    background-color: ${colors.nightBlue900};
    box-shadow: ${shadows.contactBox};
    overflow: hidden;

    h2 {
      position: relative;
      z-index: 1;
      margin-bottom: ${rem(spaces.epsilon)};
      line-height: ${rem(33)};
      color: ${colors.white};

      ${mixins.MQ('medium')} {
        max-width: 50%;
        margin-bottom: 0;
        line-height: ${rem(40)};
      }
    }

    footer {
      flex: 1 0 auto;
      display: flex;
      justify-content: center;
    }

    canvas {
      filter: brightness(0) invert(1) opacity(15%);
    }

    ${theme?.stylesExtension?.newsletter?.content}
  `,
);

const Newsletter = ({ header, button, newsletterHref }: NewsletterProps) => {
  const link = {
    title: button,
    href: newsletterHref || '',
  };

  return (
    <NewsletterContent>
      <H2>{header}</H2>
      {button ? (
        <footer>
          <StyledButton
            link={link}
            buttonText={button}
            iconAfter="AnimatedIconArrowRight"
            className="animate"
          />
        </footer>
      ) : null}
      <StyledDecoration>
        <Hidden below="large">
          <MagneticField isVisible={true} />
        </Hidden>
      </StyledDecoration>
    </NewsletterContent>
  );
};

export interface NewsletterProps {
  /** Newsletter header */
  header: string;
  /** Newsletter button1 */
  button?: string;
  newsletterHref?: string;
}

export default Newsletter;
